/**
 * Portal Help Section Component
 *
 * Provides clear instructions on how to use the Conditions Portal:
 * - Step-by-step process
 * - File format requirements
 * - Size limits
 * - What happens after upload
 */

import { Lightbulb, FileText, Upload, Send, CheckCircle } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';

interface PortalHelpSectionProps {
  isCollapsible?: boolean;
}

export const PortalHelpSection = ({ isCollapsible = false }: PortalHelpSectionProps) => {
  const content = (
    <div className="space-y-4">
      {/* Steps */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <StepItem
          step={1}
          icon={<FileText className="h-4 w-4" />}
          title="Review"
          description="Review each condition listed above"
        />
        <StepItem
          step={2}
          icon={<Upload className="h-4 w-4" />}
          title="Download"
          description="Download templates if available"
        />
        <StepItem
          step={3}
          icon={<Upload className="h-4 w-4" />}
          title="Upload"
          description="Upload your documents"
        />
        <StepItem
          step={4}
          icon={<Send className="h-4 w-4" />}
          title="Submit"
          description="Click submit when ready"
        />
      </div>

      {/* File Requirements */}
      <div className="flex flex-wrap items-center gap-4 text-xs text-gray-500 pt-2 border-t">
        <span className="flex items-center gap-1">
          <CheckCircle className="h-3.5 w-3.5 text-green-500" />
          Accepted: PDF, JPG, PNG
        </span>
        <span className="flex items-center gap-1">
          <CheckCircle className="h-3.5 w-3.5 text-green-500" />
          Max size: 25MB per file
        </span>
        <span className="flex items-center gap-1">
          <CheckCircle className="h-3.5 w-3.5 text-green-500" />
          Uploads go directly to lender
        </span>
      </div>
    </div>
  );

  return (
    <Card className="border-0 shadow-sm bg-gradient-to-br from-blue-50 to-indigo-50">
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <Lightbulb className="h-5 w-5 text-blue-600" />
          <h3 className="font-semibold text-gray-900">How This Works</h3>
        </div>
        {content}
      </CardContent>
    </Card>
  );
};

interface StepItemProps {
  step: number;
  icon: React.ReactNode;
  title: string;
  description: string;
}

const StepItem = ({ step, icon, title, description }: StepItemProps) => (
  <div className="flex items-start gap-2">
    <div className="flex items-center justify-center h-6 w-6 rounded-full bg-blue-600 text-white text-xs font-bold shrink-0">
      {step}
    </div>
    <div>
      <p className="font-medium text-gray-900 text-sm">{title}</p>
      <p className="text-xs text-gray-500">{description}</p>
    </div>
  </div>
);

export default PortalHelpSection;
